/**
 * AudioListenerBinding — AUDIO-01 / AUDIO-02 single-instance listener guard.
 *
 * Plan 05-05 Task 1 contract. Mounts THREE.AudioListener on the active R3F
 * camera exactly once. The listener is THE world-space "ear" that every
 * PositionalAudio source attenuates against — browser Web Audio API
 * requires a single AudioListener per context, and three's
 * PositionalAudio.setRefDistance / setRolloffFactor compute falloff against
 * whichever listener is mounted on the camera.
 *
 * Pitfall 4 closure: drei's <PositionalAudio> adds a listener internally on
 * first mount — if we ALSO add one naively, two exist. AND React StrictMode
 * double-invokes effects in dev, which would double-add without a guard.
 *
 * Guard strategy:
 *   - find-before-create: on mount, scan camera.children for an existing
 *     THREE.AudioListener. If found, do NOTHING (drei already wired).
 *   - If absent, create one and add; stash in a ref so cleanup knows this
 *     component owns it.
 *   - On unmount: only remove the listener IF the ref's value was added by
 *     this component instance (ownership guard). If a different component
 *     added the listener before us, we leave it.
 *
 * Mount this at SceneGraph root, BEFORE any PositionalAudio descendants, so
 * the listener exists before drei's <PositionalAudio> ever runs its internal
 * "attach if missing" check. Defense-in-depth: even if drei races us, the
 * find-before-create guard prevents duplicates.
 *
 * Returns null — this is a side-effect component only.
 */
import { useEffect, useRef } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';

export function AudioListenerBinding(): null {
  const { camera } = useThree();
  // Tracks the listener this component instance added (null if we reused an
  // existing one). Cleanup only removes when this ref is non-null.
  const ownedListenerRef = useRef<THREE.AudioListener | null>(null);

  useEffect(() => {
    // Find-before-create: is there already a listener on this camera?
    const existing = camera.children.find(
      (c): c is THREE.AudioListener => c instanceof THREE.AudioListener,
    );

    if (existing) {
      // Someone else (drei PositionalAudio, a prior mount) already attached.
      // Don't add another; don't claim ownership.
      ownedListenerRef.current = null;
    } else {
      const listener = new THREE.AudioListener();
      camera.add(listener);
      ownedListenerRef.current = listener;
    }

    return () => {
      // Ownership guard: only remove if we created this listener.
      if (ownedListenerRef.current) {
        camera.remove(ownedListenerRef.current);
        ownedListenerRef.current = null;
      }
    };
  }, [camera]);

  return null;
}
